/**
 * Page de démonstration PMSI
 * Affiche le service, les soignants, les chambres, les patients et leurs prescriptions
 */

import { useEffect, useState } from 'react';
import { demoService } from '@/shared/services/demoService';
import type { PmsiData, Chambre, Patient, Prescription, Soignant } from '@/shared/types/pmsi';

export function PmsiDemoPage() {
  const [data, setData] = useState<PmsiData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    demoService
      .getPmsi()
      .then(setData)
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center py-5">
        <div className="spinner-border text-primary me-3" role="status" />
        <span className="text-muted">Chargement des données PMSI...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger d-flex align-items-center gap-2">
        <i className="bi bi-exclamation-triangle-fill fs-5" />
        <span>Erreur : {error}</span>
      </div>
    );
  }

  if (!data) return null;

  const { service } = data;

  return (
    <div className="container-fluid">
      {/* En-tête service */}
      <div className="d-flex align-items-center gap-3 mb-4">
        <div className="rounded-circle bg-primary bg-opacity-10 p-3">
          <i className="bi bi-hospital fs-4 text-primary" />
        </div>
        <div>
          <h2 className="mb-0 fw-bold">{service.nom}</h2>
          <span className="text-muted small">{service.id}</span>
        </div>
        <span className="badge bg-success ms-auto fs-6">
          <i className="bi bi-people me-1" />
          {service.chambres.reduce((acc, c) => acc + c.patients.length, 0)} patients admis
        </span>
      </div>

      {/* Équipe soignante */}
      <SoignantsPanel soignants={service.soignants} />

      {/* Chambres */}
      <div className="row g-3 mt-1">
        {service.chambres.map((chambre) => (
          <ChambreCard key={chambre.numero} chambre={chambre} soignants={service.soignants} />
        ))}
      </div>
    </div>
  );
}

function SoignantsPanel({ soignants }: { soignants: Soignant[] }) {
  return (
    <div className="card mb-3 border-0 shadow-sm">
      <div className="card-header bg-transparent border-bottom py-3">
        <h6 className="mb-0 fw-semibold">
          <i className="bi bi-person-badge me-2 text-primary" />
          Équipe soignante
        </h6>
      </div>
      <div className="card-body py-3">
        <div className="d-flex gap-3 flex-wrap">
          {soignants.map((s) => (
            <div key={s.id} className="d-flex align-items-center gap-2 px-3 py-2 rounded-3 bg-light">
              <i className={`bi ${s.role === 'medecin' ? 'bi-clipboard2-pulse' : 'bi-heart-pulse'} text-${s.role === 'medecin' ? 'primary' : 'success'}`} />
              <div>
                <div className="fw-semibold small">{s.prenom} {s.nom}</div>
                <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                  {s.role === 'medecin' ? 'Médecin' : 'Infirmière'}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function ChambreCard({ chambre, soignants }: { chambre: Chambre; soignants: Soignant[] }) {
  const soignantMap = Object.fromEntries(soignants.map((s) => [s.id, s]));

  return (
    <div className={`col-12 col-lg-${chambre.type === 'simple' ? '4' : '8'}`}>
      <div className="card border-0 shadow-sm h-100">
        <div className="card-header bg-transparent border-bottom py-3 d-flex align-items-center justify-content-between">
          <div className="d-flex align-items-center gap-2">
            <i className="bi bi-door-open text-secondary" />
            <span className="fw-semibold">Chambre {chambre.numero}</span>
          </div>
          <span className={`badge bg-${chambre.type === 'simple' ? 'secondary' : 'info'} bg-opacity-75`}>
            {chambre.type === 'simple' ? '1 lit' : '2 lits'}
          </span>
        </div>
        <div className="card-body p-3">
          {chambre.patients.length === 0 ? (
            <p className="text-muted text-center py-3 mb-0">
              <i className="bi bi-dash-circle me-1" />
              Chambre vide
            </p>
          ) : (
            <div className="row g-3">
              {chambre.patients.map((patient) => (
                <PatientCard
                  key={patient.id}
                  patient={patient}
                  prescripteur={soignantMap[patient.sejour.ordonnance.prescripteur_id]}
                  isDouble={chambre.type === 'double'}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function PatientCard({
  patient,
  prescripteur,
  isDouble,
}: {
  patient: Patient;
  prescripteur: Soignant | undefined;
  isDouble: boolean;
}) {
  const [open, setOpen] = useState(false);
  const { ordonnance } = patient.sejour;

  return (
    <div className={isDouble ? 'col-12 col-md-6' : 'col-12'}>
      <div className="border rounded-3 p-3 bg-white">
        {/* Identité patient */}
        <div className="d-flex align-items-start justify-content-between mb-2">
          <div className="d-flex align-items-center gap-2">
            <div className="rounded-circle bg-primary bg-opacity-10 d-flex align-items-center justify-content-center"
              style={{ width: 36, height: 36 }}>
              <i className="bi bi-person text-primary" />
            </div>
            <div>
              <div className="fw-semibold">{patient.prenom} {patient.nom}</div>
              <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                Né(e) le {new Date(patient.date_naissance).toLocaleDateString('fr-FR')}
              </div>
            </div>
          </div>
          <span className="badge bg-success bg-opacity-75">
            <i className="bi bi-check-circle me-1" />
            {patient.statut}
          </span>
        </div>

        {/* Séjour */}
        <div className="text-muted mb-3" style={{ fontSize: '0.78rem' }}>
          <i className="bi bi-calendar3 me-1" />
          Entrée le {new Date(patient.sejour.date_entree).toLocaleDateString('fr-FR')}
          {prescripteur && (
            <span className="ms-2">
              · <i className="bi bi-clipboard2-pulse me-1" />
              Dr {prescripteur.nom}
            </span>
          )}
        </div>

        {/* Toggle ordonnance */}
        <button
          className="btn btn-outline-primary btn-sm w-100"
          onClick={() => setOpen((o) => !o)}
        >
          <i className={`bi bi-${open ? 'chevron-up' : 'capsule'} me-2`} />
          {ordonnance.prescriptions.length} prescription(s)
          <i className={`bi bi-chevron-${open ? 'up' : 'down'} ms-2`} />
        </button>

        {/* Liste prescriptions */}
        {open && (
          <div className="mt-2">
            {ordonnance.prescriptions.map((p) => (
              <PrescriptionRow key={p.id} prescription={p} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function PrescriptionRow({ prescription: p }: { prescription: Prescription }) {
  return (
    <div className="d-flex align-items-start gap-2 py-2 border-top">
      <i className="bi bi-capsule text-success mt-1" style={{ fontSize: '0.9rem' }} />
      <div className="flex-grow-1">
        <div className="fw-semibold small">{p.medicament} <span className="text-muted fw-normal">{p.dosage}</span></div>
        <div className="text-muted" style={{ fontSize: '0.75rem' }}>
          <span className="me-2"><i className="bi bi-arrow-right me-1" />{p.voie}</span>
          <span className="me-2"><i className="bi bi-clock me-1" />{p.frequence}</span>
          <span>{p.horaires.join(', ')}</span>
        </div>
      </div>
      <span className="badge bg-primary bg-opacity-10 text-primary border border-primary border-opacity-25" style={{ fontSize: '0.7rem' }}>
        régulier
      </span>
    </div>
  );
}
